import React, { useState, useEffect } from 'react';
import { Button, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import pdfGenerator from '../services/pdfGenerator';
import RelatorioFiltrosModal from './RelatorioFiltrosModal';
import { useContext } from 'react';
import { GlobalContext } from '../context/globalContext';
import FornecedoresService from '../services/dashboard/fornecedores.service';
import { Fornecedor } from '../types/dashboard/fornecedores.types';

interface PdfButtonProps {
  clienteId: string;
  tipo: 'dashboard' | 'fornecedor';
  fornecedorId?: string;
  filtros?: any;
  texto?: string;
  fornecedorSelecionado?: string;
}

const PdfButton: React.FC<PdfButtonProps> = ({
  clienteId,
  tipo,
  fornecedorId,
  filtros = {},
  texto = 'Gerar PDF',
  fornecedorSelecionado
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [fornecedoresList, setFornecedoresList] = useState<Fornecedor[]>([]);
  const globalContext = useContext(GlobalContext);
  const currentCliente = globalContext?.currentCliente;

  // Função robusta para obter o nome do cliente de várias fontes possíveis
  const getClientName = () => {
    // Nome do cliente do contexto global (primeira prioridade)
    if (currentCliente) {
      if (currentCliente.razao_social) {
        return currentCliente.razao_social;
      }
      if (currentCliente.razaoSocial) {
        return currentCliente.razaoSocial;
      }
      if (currentCliente.nome) {
        return currentCliente.nome;
      }
      if (currentCliente.name) {
        return currentCliente.name;
      }
    }
    
    // Verificar localStorage como fallback (segunda prioridade)
    try {
      const localStorageCliente = localStorage.getItem('currentCliente');
      if (localStorageCliente) {
        const parsedCliente = JSON.parse(localStorageCliente);
        if (parsedCliente) {
          if (parsedCliente.razao_social) {
            return parsedCliente.razao_social;
          }
          if (parsedCliente.razaoSocial) {
            return parsedCliente.razaoSocial;
          }
          if (parsedCliente.nome) {
            return parsedCliente.nome;
          }
          if (parsedCliente.name) {
            return parsedCliente.name;
          }
        }
      }
    } catch (error) {
      // Silenciar erro
    }
    
    // Se nada mais funcionar, retornar o valor padrão
    return 'Cliente';
  };

  const handleClick = async () => {
    // Carregar lista de fornecedores antes de abrir o modal
    try {
      setLoading(true);
      const response = await FornecedoresService.getFornecedores({
        page: 1,
        limit: 100,
        cliente_id: clienteId
      });
      
      if (response && response.data && Array.isArray(response.data)) {
        setFornecedoresList(response.data);
      } else if (response && Array.isArray(response)) {
        setFornecedoresList(response);
      }
      
      // Abrir o modal
      setModalVisible(true);
    } catch (error) {
      message.error("Não foi possível carregar a lista de fornecedores");
    } finally {
      setLoading(false);
    }
  };

  const gerarEVisualizar = async (filtrosRelatorio: any) => {
    try {
      setLoading(true);
      
      // IMPORTANTE: Obter o nome do cliente ANTES de construir os parâmetros
      const nomeClienteAtual = getClientName();
      
      // SOLUÇÃO: Estruturação dos parâmetros de forma consistente
      const params: any = {
        clienteId,
        // Fornecer fornecedorId apenas se for um relatório de fornecedor
        ...(tipo === 'fornecedor' ? { fornecedorId } : {})
      };
      
      // Construir os filtros da mesma maneira para ambos os tipos
      params.filtros = {
        ...filtros,
        ...filtrosRelatorio,
        // Garantir que estas propriedades específicas sejam incluídas
        nomeCliente: nomeClienteAtual,
        tipoVisualizacao: tipo === 'fornecedor' ? 'detalhada' : 'consolidada'
      };
      
      // Se fornecedorId foi fornecido nos filtros do relatório, sobrescrever
      if (filtrosRelatorio.fornecedorId) {
        params.filtros.fornecedorId = filtrosRelatorio.fornecedorId;
        // Se foi fornecido um fornecedor específico, também definir como fornecedorId principal para relatórios detalhados
        if (filtrosRelatorio.tipoVisualizacao === 'detalhada') {
          params.fornecedorId = filtrosRelatorio.fornecedorId;
        }
      }
      
      // Gerar e visualizar o PDF
      await pdfGenerator.gerarEVisualizar(tipo, params);
      
      message.success('Relatório gerado com sucesso');
      
      // Fechar modal após sucesso
      setModalVisible(false);
    } catch (error) {
      if (error instanceof Error) {
        message.error(`Erro ao visualizar PDF: ${error.message}`);
      } else {
        message.error('Erro ao visualizar PDF');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={<FilePdfOutlined />}
        loading={loading}
        onClick={handleClick}
      >
        {texto}
      </Button>
      
      <RelatorioFiltrosModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onGenerate={gerarEVisualizar}
        clienteId={clienteId}
        clienteNome={getClientName()}
        fornecedoresList={fornecedoresList}
        filtrosAtuais={filtros}
        tipo={tipo}
        fornecedorId={fornecedorId}
        fornecedorPredefinido={fornecedorSelecionado}
      />
    </>
  );
};

export default PdfButton;